import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type RefObject, useId } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  renameStudyDocument,
  STUDY_DOCUMENTS_QUERY_KEY,
  STUDY_MATERIAL_NAME_MAX,
} from './studyApi';
import { StudyMaterialDialog } from './StudyMaterialDialog';

/**
 * Rename a material (owner feedback 2026-10-08). "Use the original name" sends null, so the title
 * goes back to the analysed topic or the file name. The server checks the same rules.
 */

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F-\u009F]/;

const renameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Type a name, or use the original name.')
    .max(STUDY_MATERIAL_NAME_MAX, `Use ${STUDY_MATERIAL_NAME_MAX} characters or fewer.`)
    .refine((value) => !CONTROL_CHARACTERS.test(value), 'Remove special characters.'),
});
type RenameForm = z.infer<typeof renameSchema>;

/** Query keys that show a material title; all are refreshed after a rename. */
const MATERIAL_QUERY_KEYS = [
  STUDY_DOCUMENTS_QUERY_KEY,
  ['study-documents-page'],
  ['study-materials-library'],
  ['study-document'],
] as const;

export function StudyMaterialRenameDialog({
  documentId,
  currentTitle,
  hasChosenName,
  returnFocusRef,
  onClose,
}: {
  documentId: string;
  currentTitle: string;
  /** True when the learner already chose a name, so "Use the original name" can undo it. */
  hasChosenName: boolean;
  returnFocusRef?: RefObject<HTMLElement>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const errorId = useId();
  const form = useForm<RenameForm>({
    resolver: zodResolver(renameSchema),
    defaultValues: { name: currentTitle },
  });
  const rename = useMutation({
    mutationFn: (name: string | null) => renameStudyDocument(documentId, name),
    onSuccess: async () => {
      await Promise.all(
        MATERIAL_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      onClose();
    },
  });
  const fieldError = form.formState.errors.name?.message;

  return (
    <StudyMaterialDialog title="Rename material" onClose={onClose} returnFocusRef={returnFocusRef}>
      <form
        noValidate
        onSubmit={form.handleSubmit((values) => rename.mutate(values.name))}
        className="space-y-4"
      >
        <label className="block text-sm font-extrabold text-ink">
          Name
          <input
            type="text"
            autoComplete="off"
            data-autofocus
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? errorId : undefined}
            {...form.register('name')}
            className="mt-1 block h-11 w-full rounded-xl border border-hairline bg-canvas px-3 text-base font-medium text-ink outline-none focus:border-brand-sky"
          />
        </label>
        {fieldError && (
          <p id={errorId} role="alert" className="text-sm font-bold text-brand-coral">
            {fieldError}
          </p>
        )}
        {rename.isError && (
          <p role="alert" className="text-sm font-bold text-brand-coral">
            {rename.error instanceof Error ? rename.error.message : 'The name was not saved.'}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={rename.isPending}
            className="btn-pill-primary disabled:opacity-50"
          >
            {rename.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose} className="btn-pill-secondary">
            Cancel
          </button>
        </div>
        {hasChosenName && (
          <button
            type="button"
            disabled={rename.isPending}
            onClick={() => rename.mutate(null)}
            className="min-h-11 text-sm font-bold text-brand-sky underline-offset-2 hover:underline disabled:opacity-50"
          >
            Use the original name
          </button>
        )}
      </form>
    </StudyMaterialDialog>
  );
}
